import { useState } from "react";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { EMAIL_REGEX } from "@memento/library/email";
import { LabeledInput } from "@memento/ui/Form/LabeledInput";
import { LoadingButton } from "@memento/ui/LoadingButton";
import { withAdsDisabled } from "src/components/Ads/withAdsDisabled";
import { getProduct, sendAnalyticsEvent } from "src/libs/analytics-lib";
import { useAuth } from "src/hooks/useAuth";
import ERRORS from "src/errors";
import { toastError } from "src/libs/toast-lib";
import { useRouter } from "next/router";
import { getQueryParam } from "src/libs/nextLib";
import { AuthLink } from "./AuthLink";
import { SocialLoginButton, SocialPlatforms } from "./SocialLoginButton";
import config from "src/config";
import { FederatedRedirectOptions } from "src/contexts/AuthProvider";
import { useFederatedRedirectOptions } from "src/hooks/useFederatedRedirectOptions";
import { cn } from "src/utils/cn";
import { guardEntryPoint } from "@memento/types/subscription";

const labelStyle = `text-sm font-normal`;
const inputStyle = `border-gray-200 border text-sm`;

type ErrorWithCode = {
  code?: string;
  message?: string;
};

type LoginFormValues = {
  email: string;
  password: string;
};

const schema = z.object({
  email: z
    .string()
    .regex(EMAIL_REGEX, { message: "Please provide a valid email address." })
    .nonempty({ message: "Please enter your email address." }),
  password: z.string().nonempty({ message: "Please enter your password." }),
});

export type LoginProps = {
  clearLoginCache?: boolean;
  onAccountUnconfirmed?: (email: string) => void;
  onModalSignupClick?: null | (() => void);
  federatedRedirectOptions?: FederatedRedirectOptions;
};

export const Login = ({
  clearLoginCache = true,
  onAccountUnconfirmed = () => {},
  onModalSignupClick = null,
  federatedRedirectOptions,
}: LoginProps) => {
  const finalFederatedRedirectOptions = useFederatedRedirectOptions(
    federatedRedirectOptions
  );

  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const { login, loginFederated } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: getQueryParam(router.query.email) || "",
      password: "",
    },
  });

  const handleFacebookLogin = async () => {
    await loginFederated(
      CognitoHostedUIIdentityProvider.Facebook,
      finalFederatedRedirectOptions
    );
  };

  const handleAppleLogin = async () => {
    await loginFederated(
      CognitoHostedUIIdentityProvider.Apple,
      finalFederatedRedirectOptions
    );
  };

  const handleLoginSubmit: SubmitHandler<LoginFormValues> = async ({
    email,
    password,
  }) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const user = await login(email, password, clearLoginCache);
      const entryPoint = guardEntryPoint(
        getQueryParam(router.query.entryPoint) || "",
        "group-videos"
      );

      sendAnalyticsEvent({
        event: "LoginWithEmail",
        product: getProduct(entryPoint),
        userId: user?.username,
        // we can't know the tierLabel at this point
        tierLabel: undefined,
      });

      // The redirect is handled within GuardUnauthenticated
    } catch (e) {
      const err = e as ErrorWithCode;

      // If user's not verified, hide error message and go to verification screen
      if (err?.code === ERRORS.USER_NOT_VERIFIED) {
        onAccountUnconfirmed(email);
      } else if (err?.message) {
        toastError(err.message);
      } else if (!(err instanceof Error)) {
        toastError(
          "Something went wrong and we were unable to log you in. Please try again."
        );
      }

      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "mx-auto max-w-sm rounded-2xl border-gray-300 bg-white text-center",
        onModalSignupClick ? `mt-4` : `mt-8 p-10 shadow-xl`
      )}
    >
      <div className="flex flex-row justify-between space-x-1">
        <h2 className="text-3xl">Login</h2>
        <span className="self-center text-xs">
          {onModalSignupClick ? (
            <button
              type="button"
              onClick={onModalSignupClick}
              className="text-red-600"
            >
              New? Sign up
            </button>
          ) : (
            <AuthLink type="signup">New? Sign up</AuthLink>
          )}
        </span>
      </div>
      <div className="mt-6 space-y-2">
        <SocialLoginButton
          platform={SocialPlatforms.FACEBOOK}
          authType="Login"
          onClick={handleFacebookLogin}
          width="full"
        />
        {config.apple.signInEnabled ? (
          <SocialLoginButton
            platform={SocialPlatforms.APPLE}
            authType="Login"
            onClick={handleAppleLogin}
            width="full"
          />
        ) : null}
      </div>
      <div className="mt-6 mb-4 flex justify-self-start text-sm font-medium">
        Login with Email
      </div>
      <form onSubmit={handleSubmit(handleLoginSubmit)}>
        <div className="space-y-4">
          <LabeledInput
            data-test="input-email"
            label="Email"
            id="email"
            type="email"
            error={errors.email?.message}
            className={inputStyle}
            labelStyle={labelStyle}
            {...register("email")}
          />
          <LabeledInput
            data-test="input-password"
            label="Password"
            id="password"
            type="password"
            error={errors.password?.message}
            className={inputStyle}
            labelStyle={labelStyle}
            {...register("password")}
          />
        </div>
        <div className="mt-4">
          <LoadingButton
            data-test="login-button"
            type="submit"
            isLoading={isLoading}
            text="Login"
            variant="primary"
            width="full"
            loadingText="Logging in…"
          />
        </div>
        <div className="mt-5 text-xs">
          <AuthLink type="reset">Forgot your password?</AuthLink>
        </div>
      </form>
    </div>
  );
};

export const LoginWithAdsDisabled = withAdsDisabled(Login);
